"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PluggableBubbleChart = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2022 GoodData Corporation
var sdk_ui_1 = require("@gooddata/sdk-ui");
var react_1 = tslib_1.__importDefault(require("react"));
var bucket_1 = require("../../../constants/bucket");
var supportedProperties_1 = require("../../../constants/supportedProperties");
var uiConfig_1 = require("../../../constants/uiConfig");
var bucketConfig_1 = require("../../../utils/bucketConfig");
var bucketHelper_1 = require("../../../utils/bucketHelper");
var propertiesHelper_1 = require("../../../utils/propertiesHelper");
var sort_1 = require("../../../utils/sort");
var bubbleChartUiConfigHelper_1 = require("../../../utils/uiConfigHelpers/bubbleChartUiConfigHelper");
var BubbleChartConfigurationPanel_1 = tslib_1.__importDefault(require("../../configurationPanels/BubbleChartConfigurationPanel"));
var PluggableBaseChart_1 = require("../baseChart/PluggableBaseChart");
var cloneDeep_1 = tslib_1.__importDefault(require("lodash/cloneDeep"));
var includes_1 = tslib_1.__importDefault(require("lodash/includes"));
var set_1 = tslib_1.__importDefault(require("lodash/set"));
/**
 * PluggableBubbleChart
 *
 * ## Buckets
 *
 * | Name             | Id                 | Accepts             |
 * |------------------|--------------------|---------------------|
 * | Measure (X-axis) | measures           | measures only       |
 * | Measure (Y-axis) | secondary_measures | measures only       |
 * | Measure (Size)   | tertiary_measures  | measures only       |
 * | ViewBy           | view               | attributes or dates |
 *
 * ### Bucket axioms
 *
 * - |MeasureX| ≤ 1
 * - |MeasureY| ≤ 1
 * - |MeasureSize| ≤ 1
 * - |ViewBy| ≤ 1
 * - |MeasureX| + |MeasureY| + |MeasureSize| ≥ 1
 *
 * ## Dimensions
 *
 * The PluggableBubbleChart always creates the same two dimensional execution.
 *
 * - ⊤ ⇒ [[...ViewBy], [MeasureGroupIdentifier]]
 *
 * ## Sorts
 *
 * The PluggableBubbleChart does not use any sorts.
 */
var PluggableBubbleChart = /** @class */ (function (_super) {
    tslib_1.__extends(PluggableBubbleChart, _super);
    function PluggableBubbleChart(props) {
        var _this = _super.call(this, props) || this;
        _this.type = sdk_ui_1.VisualizationTypes.BUBBLE;
        _this.supportedPropertiesList = supportedProperties_1.BUBBLE_CHART_SUPPORTED_PROPERTIES;
        _this.initializeProperties(props.visualizationProperties);
        return _this;
    }
    PluggableBubbleChart.prototype.getExtendedReferencePoint = function (referencePoint) {
        var clonedReferencePoint = cloneDeep_1.default(referencePoint);
        var newReferencePoint = tslib_1.__assign(tslib_1.__assign({}, clonedReferencePoint), { uiConfig: cloneDeep_1.default(uiConfig_1.DEFAULT_BUBBLE_CHART_CONFIG) });
        newReferencePoint = bucketHelper_1.removeAllArithmeticMeasuresFromDerived(newReferencePoint);
        newReferencePoint = bucketHelper_1.removeAllDerivedMeasures(newReferencePoint);
        var buckets = bucketHelper_1.limitNumberOfMeasuresInBuckets(clonedReferencePoint.buckets, 3);
        // limit number of measures in the ref point here?
        // Check if there are three measure buckets
        var measuresBucketItems = bucketHelper_1.getPreferredBucketItems(buckets, [sdk_ui_1.BucketNames.MEASURES], [bucket_1.METRIC]);
        var secondaryMeasuresBucketItems = bucketHelper_1.getPreferredBucketItems(buckets, [sdk_ui_1.BucketNames.SECONDARY_MEASURES], [bucket_1.METRIC]);
        var tertiaryMeasuresBucketItems = bucketHelper_1.getPreferredBucketItems(buckets, [sdk_ui_1.BucketNames.TERTIARY_MEASURES], [bucket_1.METRIC]);
        var allMeasures = bucketHelper_1.getAllMeasures(buckets);
        // skip first to reserve first items to be picked later
        var secondaryAndTertiaryItems = tslib_1.__spreadArrays(secondaryMeasuresBucketItems.slice(0, 1), tertiaryMeasuresBucketItems.slice(0, 1));
        var measures = measuresBucketItems.length > 0
            ? measuresBucketItems.slice(0, 1)
            : allMeasures.filter(function (measure) { return !includes_1.default(secondaryAndTertiaryItems, measure); }).slice(0, 1);
        var secondaryMeasures = secondaryMeasuresBucketItems.length > 0
            ? secondaryMeasuresBucketItems.slice(0, 1)
            : allMeasures
                .filter(function (measure) {
                return !includes_1.default(tslib_1.__spreadArrays(measures, tertiaryMeasuresBucketItems.slice(0, 1)), measure);
            })
                .slice(0, 1);
        var tertiaryMeasures = tertiaryMeasuresBucketItems.length > 0
            ? tertiaryMeasuresBucketItems.slice(0, 1)
            : allMeasures
                .filter(function (measure) { return !includes_1.default(tslib_1.__spreadArrays(measures, secondaryMeasures), measure); })
                .slice(0, 1);
        set_1.default(newReferencePoint, bucket_1.BUCKETS, [
            {
                localIdentifier: sdk_ui_1.BucketNames.MEASURES,
                items: measures,
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.SECONDARY_MEASURES,
                items: secondaryMeasures,
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.TERTIARY_MEASURES,
                items: tertiaryMeasures,
            },
            {
                localIdentifier: sdk_ui_1.BucketNames.VIEW,
                items: bucketHelper_1.getAllAttributeItems(buckets).slice(0, 1),
            },
        ]);
        newReferencePoint = bubbleChartUiConfigHelper_1.setBubbleChartUiConfig(newReferencePoint, this.intl, this.type);
        newReferencePoint = bucketConfig_1.configurePercent(newReferencePoint, true);
        newReferencePoint = bucketConfig_1.configureOverTimeComparison(newReferencePoint, !!this.featureFlags["enableWeekFilters"]);
        newReferencePoint = propertiesHelper_1.getReferencePointWithSupportedProperties(newReferencePoint, this.supportedPropertiesList);
        newReferencePoint = sort_1.removeSort(newReferencePoint);
        return Promise.resolve(bucketHelper_1.sanitizeFilters(newReferencePoint));
    };
    PluggableBubbleChart.prototype.renderConfigurationPanel = function (insight) {
        var configPanelElement = this.getConfigPanelElement();
        if (configPanelElement) {
            this.renderFun(react_1.default.createElement(BubbleChartConfigurationPanel_1.default, { locale: this.locale, references: this.references, properties: this.visualizationProperties, propertiesMeta: this.propertiesMeta, insight: insight, colors: this.colors, pushData: this.handlePushData, type: this.type, isError: this.getIsError(), isLoading: this.isLoading, featureFlags: this.featureFlags }), configPanelElement);
        }
        return null;
    };
    return PluggableBubbleChart;
}(PluggableBaseChart_1.PluggableBaseChart));
exports.PluggableBubbleChart = PluggableBubbleChart;
//# sourceMappingURL=PluggableBubbleChart.js.map